<template>
  <div class="selector-hora">
    <div
      v-for="columna in ['horas', 'minutos']"
      :key="columna"
      class="d-flex align-center"
    >
      <div v-if="columna === 'minutos'" class="separador">:</div>
      <div class="rueda">
      <!-- Lo que se va escribiendo con el teclado, sobre la rueda. -->
      <div v-if="escritura && escritura.columna === columna" class="escritura">
        <span>{{ escritura.texto }}</span><span class="cursor"></span>
      </div>
      <div
        class="columna"
        :class="{
          arrastrando: arrastre && arrastre.columna === columna,
          escribiendo: escritura && escritura.columna === columna,
        }"
        :ref="columna"
        tabindex="0"
        :aria-label="columna === 'horas' ? 'Hora' : 'Minutos'"
        @scroll="alDesplazar(columna)"
        @mousedown="empezarArrastre(columna, $event)"
        @keydown.up.prevent="mover(columna, -1)"
        @keydown.down.prevent="mover(columna, 1)"
        @keydown="alTeclear(columna, $event)"
        @keydown.esc="cerrarEscritura"
        @dblclick="abrirEscritura(columna)"
        @blur="alPerderFoco(columna)"
      >
        <div class="relleno"></div>
        <div
          v-for="(opcion, i) in opciones[columna]"
          :key="opcion"
          class="opcion"
          :class="{ elegida: i === indice[columna] }"
          @click="alHacerClic(columna, i)"
        >
          {{ dos(opcion) }}
        </div>
        <div class="relleno"></div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
// Alto en px de cada opción; tiene que coincidir con el del estilo.
const ALTO = 48;

// Milisegundos sin desplazarse antes de dar por elegida la opción del centro.
const ESPERA = 120;

// Píxeles que hay que mover el mouse para que sea un arrastre y no un clic.
const UMBRAL_ARRASTRE = 4;

// Milisegundos que se espera el segundo dígito al escribir un número.
const ESPERA_TECLADO = 1200;

// Milisegundos que sigue a la vista un número ya escrito completo.
const ESPERA_ESCRITO = 450;

function aMinutos(texto) {
  const partes = /^(\d{1,2}):(\d{2})/.exec(texto || "");
  return partes ? Number(partes[1]) * 60 + Number(partes[2]) : null;
}

function masCercano(lista, valor) {
  return lista.reduce((mejor, actual) =>
    Math.abs(actual - valor) < Math.abs(mejor - valor) ? actual : mejor
  );
}

// Selector de hora de dos ruedas (horas y minutos), como el de la alarma del
// teléfono: se desplaza cada columna (con la rueda del mouse, el dedo o
// arrastrando con el mouse) y queda elegida la opción del centro, o se hace
// clic en un número. Con una columna enfocada también se puede escribir el
// número (el doble clic deja a la vista dónde se escribe): queda la opción
// más cercana. El valor es un texto "HH:mm"; `min` y
// `max` lo acotan.
export default {
  props: {
    value: { type: String, default: null },
    min: { type: String, default: "00:00" },
    max: { type: String, default: "23:59" },
    // Cada cuántos minutos hay una opción.
    paso: { type: Number, default: 5 },
  },
  data: () => ({
    hora: null,
    minuto: null,
    // La opción que está al centro de cada columna, también mientras se desplaza.
    indice: { horas: 0, minutos: 0 },
    espera: { horas: null, minutos: null },
    // { columna, y, scroll, movido } mientras se arrastra una columna con el mouse.
    arrastre: null,
    // El clic que sigue a un arrastre no elige nada.
    ignorarClic: false,
    // Los dígitos escritos en la columna enfocada, a la espera del segundo.
    tecleado: "",
    esperaTeclado: null,
    // { columna, texto, completo }: lo escrito que se muestra sobre la rueda.
    escritura: null,
  }),
  computed: {
    limites() {
      const min = aMinutos(this.min);
      const max = aMinutos(this.max);
      return { min: min === null ? 0 : min, max: max === null ? 24 * 60 - 1 : max };
    },
    opciones() {
      const { min, max } = this.limites;
      const horas = [];
      for (let h = Math.floor(min / 60); h <= Math.floor(max / 60); h++) {
        horas.push(h);
      }
      // Los minutos que caben en el rango para la hora elegida.
      const minutos = [];
      for (let m = 0; m < 60; m += this.paso) {
        const total = this.hora * 60 + m;
        if (total >= min && total <= max) {
          minutos.push(m);
        }
      }
      return { horas, minutos: minutos.length ? minutos : [0] };
    },
  },
  watch: {
    value(valor) {
      if (aMinutos(valor) !== this.hora * 60 + this.minuto) {
        this.sincronizar();
      }
    },
    min() {
      this.sincronizar();
    },
    max() {
      this.sincronizar();
    },
  },
  mounted() {
    this.sincronizar();
  },
  beforeDestroy() {
    clearTimeout(this.espera.horas);
    clearTimeout(this.espera.minutos);
    clearTimeout(this.esperaTeclado);
    this.soltarArrastre();
  },
  methods: {
    dos(numero) {
      return String(numero).padStart(2, "0");
    },

    // Deja las ruedas en el valor recibido, llevado al rango y al paso.
    sincronizar() {
      const { min, max } = this.limites;
      const recibido = aMinutos(this.value);
      const total = Math.min(Math.max(recibido === null ? min : recibido, min), max);
      this.fijar(Math.floor(total / 60), total % 60);
    },

    fijar(hora, minuto) {
      this.hora = masCercano(this.opciones.horas, hora);
      // `opciones.minutos` depende de la hora recién fijada.
      this.minuto = masCercano(this.opciones.minutos, minuto);
      this.$nextTick(() => {
        this.posicionar("horas", this.opciones.horas.indexOf(this.hora));
        this.posicionar("minutos", this.opciones.minutos.indexOf(this.minuto));
      });
      this.emitir();
    },

    posicionar(columna, i, suave) {
      this.indice[columna] = i;
      const rueda = this.$refs[columna][0];
      if (rueda) {
        rueda.scrollTo({ top: i * ALTO, behavior: suave ? "smooth" : "auto" });
      }
    },

    alDesplazar(columna) {
      const rueda = this.$refs[columna][0];
      const ultimo = this.opciones[columna].length - 1;
      const i = Math.min(Math.max(Math.round(rueda.scrollTop / ALTO), 0), ultimo);
      this.confirmarLuego(columna, i);
    },

    // Se confirma cuando la rueda deja de moverse: mientras tanto solo cambia
    // la opción resaltada.
    confirmarLuego(columna, i) {
      this.indice[columna] = i;
      clearTimeout(this.espera[columna]);
      this.espera[columna] = setTimeout(this.confirmar, ESPERA);
    },

    // Toma lo que muestran las dos ruedas, no solo la que se movió: la otra
    // puede tener un cambio todavía sin confirmar (al escribir la hora y los
    // minutos de corrido) y fijar una sola lo pisaría.
    confirmar() {
      clearTimeout(this.espera.horas);
      clearTimeout(this.espera.minutos);
      this.fijar(
        this.opciones.horas[this.indice.horas],
        this.opciones.minutos[this.indice.minutos]
      );
    },

    elegir(columna, i) {
      this.posicionar(columna, i, true);
      this.confirmarLuego(columna, i);
    },

    alHacerClic(columna, i) {
      if (!this.ignorarClic) {
        this.elegir(columna, i);
      }
    },

    // Arrastre con el mouse, como con el dedo. El táctil no pasa por aquí: ahí
    // el navegador ya desplaza la columna.
    empezarArrastre(columna, evento) {
      if (evento.button !== 0) {
        return;
      }
      this.arrastre = {
        columna,
        y: evento.clientY,
        scroll: this.$refs[columna][0].scrollTop,
        movido: false,
      };
      window.addEventListener("mousemove", this.arrastrar);
      window.addEventListener("mouseup", this.terminarArrastre);
    },

    arrastrar(evento) {
      const { columna, y, scroll } = this.arrastre;
      const distancia = evento.clientY - y;
      if (Math.abs(distancia) > UMBRAL_ARRASTRE) {
        this.arrastre.movido = true;
      }
      if (this.arrastre.movido) {
        evento.preventDefault();
        this.$refs[columna][0].scrollTop = scroll - distancia;
      }
    },

    terminarArrastre() {
      const { columna, movido } = this.arrastre;
      this.soltarArrastre();
      if (!movido) {
        return;
      }
      this.ignorarClic = true;
      setTimeout(() => (this.ignorarClic = false));
      // Al soltar, la opción más cercana al centro queda elegida.
      const rueda = this.$refs[columna][0];
      const ultimo = this.opciones[columna].length - 1;
      this.elegir(columna, Math.min(Math.max(Math.round(rueda.scrollTop / ALTO), 0), ultimo));
    },

    soltarArrastre() {
      this.arrastre = null;
      window.removeEventListener("mousemove", this.arrastrar);
      window.removeEventListener("mouseup", this.terminarArrastre);
    },

    // El doble clic abre la escritura vacía, a la espera de los dígitos; se
    // cierra con Escape o al salir de la columna.
    abrirEscritura(columna) {
      clearTimeout(this.esperaTeclado);
      this.tecleado = "";
      this.escritura = { columna, texto: "", completo: false };
    },

    cerrarEscritura() {
      clearTimeout(this.esperaTeclado);
      this.tecleado = "";
      this.escritura = null;
    },

    // Un número ya completo sigue a la vista su instante aunque el foco pase
    // a los minutos; lo que quedó a medias o vacío se cierra.
    alPerderFoco(columna) {
      this.tecleado = "";
      const abierta = this.escritura && this.escritura.columna === columna;
      if (abierta && !this.escritura.completo) {
        this.cerrarEscritura();
      }
    },

    // Escribir el número con la columna enfocada: "1" y "7" dejan las 17, "32"
    // en los minutos deja 30. Con el primer dígito ya se muestra lo más cercano.
    alTeclear(columna, evento) {
      if (!/^[0-9]$/.test(evento.key) || evento.ctrlKey || evento.metaKey || evento.altKey) {
        return;
      }
      evento.preventDefault();
      clearTimeout(this.esperaTeclado);
      this.tecleado += evento.key;
      const numero = Number(this.tecleado);
      const lista = this.opciones[columna];
      this.elegir(columna, lista.indexOf(masCercano(lista, numero)));

      // Con dos dígitos, o uno que ya no admite un segundo, el número está completo.
      const tope = columna === "horas" ? 2 : 5;
      const completo = this.tecleado.length === 2 || numero > tope;
      this.escritura = { columna, texto: this.tecleado, completo };
      if (completo) {
        this.tecleado = "";
        if (columna === "horas") {
          this.$refs.minutos[0].focus();
        }
      }
      this.esperaTeclado = setTimeout(
        () => {
          this.tecleado = "";
          this.escritura = null;
        },
        completo ? ESPERA_ESCRITO : ESPERA_TECLADO
      );
    },

    mover(columna, cuanto) {
      const ultimo = this.opciones[columna].length - 1;
      this.elegir(columna, Math.min(Math.max(this.indice[columna] + cuanto, 0), ultimo));
    },

    emitir() {
      const valor = `${this.dos(this.hora)}:${this.dos(this.minuto)}`;
      if (aMinutos(valor) !== aMinutos(this.value)) {
        this.$emit("input", valor);
      }
    },
  },
};
</script>

<style scoped>
.selector-hora {
  display: inline-flex;
  align-items: center;
  user-select: none;
}

/* Se ven tres opciones: la del centro es la elegida. */
.columna {
  height: 144px;
  width: 96px;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  outline: none;
  border-radius: 12px;
}

.rueda {
  position: relative;
}

/* Mientras se escribe, la rueda cede el lugar a lo escrito. */
.columna.escribiendo .opcion {
  opacity: 0;
}

.escritura {
  position: absolute;
  top: 48px;
  left: 12px;
  right: 12px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.25rem;
  font-weight: 700;
  border-bottom: 2px solid currentColor;
  pointer-events: none;
  z-index: 1;
}

.cursor {
  width: 2px;
  height: 34px;
  margin-left: 2px;
  background-color: currentColor;
  animation: parpadeo 1s steps(1) infinite;
}

@keyframes parpadeo {
  50% {
    opacity: 0;
  }
}

.columna::-webkit-scrollbar {
  display: none;
}

/* La columna enfocada es la que recibe lo que se escribe. */
.columna:focus {
  background-color: rgba(128, 128, 128, 0.12);
}

/* Mientras se arrastra con el mouse la rueda sigue al cursor sin saltar. */
.columna.arrastrando {
  scroll-snap-type: none;
  cursor: grabbing;
}

.columna.arrastrando .opcion {
  cursor: grabbing;
}

/* Deja que la primera y la última opción lleguen al centro. */
.relleno {
  height: 48px;
}

.opcion {
  height: 48px;
  line-height: 48px;
  font-size: 2.25rem;
  text-align: center;
  scroll-snap-align: center;
  cursor: pointer;
  opacity: 0.25;
  transition: opacity 0.15s;
}

.opcion:hover {
  opacity: 0.6;
}

.opcion.elegida {
  opacity: 1;
  font-weight: 700;
}

.separador {
  font-size: 2.25rem;
  font-weight: 700;
  padding: 0 8px;
}
</style>
